"use client";

import { useState } from "react";
import { Link2, Check } from "lucide-react";
import {
  IconX,
  IconLinkedIn,
  IconReddit,
  IconWhatsApp,
  IconTelegram,
  IconSignal,
} from "@/components/icons/SocialIcons";

interface ShareButtonsProps {
  title: string;
  slug: string;
}

const iconClass = "h-5 w-5";
const btnClass =
  "p-3 rounded-lg text-cb-muted-2 hover:text-cb-text hover:bg-cb-surface-2 transition-colors";

export function ShareButtons({ title, slug }: ShareButtonsProps) {
  const [copied, setCopied] = useState(false);

  const url = `https://cloudbreach.io/blog/${slug}`;
  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);

  function copyLink() {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <div className="flex items-center gap-1">
      <span className="text-xs text-cb-muted-2 uppercase tracking-widest font-mono mr-2">
        Found this useful?
      </span>

      <a
        href={`https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}`}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Share on X"
        className={btnClass}
      >
        <IconX className={iconClass} />
      </a>

      <a
        href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Share on LinkedIn"
        className={btnClass}
      >
        <IconLinkedIn className={iconClass} />
      </a>

      <a
        href={`https://reddit.com/submit?url=${encodedUrl}&title=${encodedTitle}`}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Share on Reddit"
        className={btnClass}
      >
        <IconReddit className={iconClass} />
      </a>

      <a
        href={`https://wa.me/?text=${encodedTitle}%20${encodedUrl}`}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Share on WhatsApp"
        className={btnClass}
      >
        <IconWhatsApp className={iconClass} />
      </a>

      <a
        href={`https://t.me/share/url?url=${encodedUrl}&text=${encodedTitle}`}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Share on Telegram"
        className={btnClass}
      >
        <IconTelegram className={iconClass} />
      </a>

      <a
        href={`sgnl://send?text=${encodedTitle}%20${encodedUrl}`}
        aria-label="Share on Signal"
        className={btnClass}
      >
        <IconSignal className={iconClass} />
      </a>

      <button
        onClick={copyLink}
        aria-label="Copy link"
        className={`${btnClass} cursor-pointer`}
      >
        {copied
          ? <Check className="h-5 w-5 text-cb-success" />
          : <Link2 className="h-5 w-5" />}
      </button>
    </div>
  );
}
